// Value boundary between Vuetify 3's VDatePicker and the input-field specs.
//
// WHY THIS EXISTS: Vuetify 2's VDatePicker modelled its value as a
// 'YYYY-MM-DD' string, so `v-model="ifield.value"` bound straight to the field
// value that every `dateField` spec stores (e.g. contractFromOpportunity's
// startDate: `default: () => new Date().toISOString().substring(0, 10)`), and
// that the validations, isDirty comparison and request body all expect.
// Vuetify 3 works in Date objects via its built-in date adapter, so the same
// binding wrote a live Date into the field the moment a day was clicked --
// observed as a Start Date reading
// "Wed Jul 15 2026 00:00:00 GMT-0700 (Pacific Daylight Time)".
//
// TIMEZONES ARE THE WHOLE DIFFICULTY. `new Date('2026-07-26')` parses as UTC
// midnight, which is 2026-07-25 17:00 in Pacific time -- so the calendar would
// open on, and highlight, the day BEFORE the stored one for every user west of
// Greenwich. Likewise `date.toISOString().substring(0, 10)` on a Date built
// from local calendar parts shifts forward for users east of it. Both
// directions therefore go through the LOCAL calendar components, never through
// UTC, so a value round-trips unchanged in any timezone.

/** Leading 'YYYY-MM-DD' of a date-only or full ISO datetime string. */
const ISO_DATE_HEAD = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * Field value -> the Date VDatePicker wants for `:model-value`.
 * Returns null (VDatePicker's "nothing selected") for the empty/absent values
 * the specs use: undefined, null and ''.
 */
export function isoDateStringToDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value !== 'string') {
    return null
  }
  const match = ISO_DATE_HEAD.exec(value)
  if (!match) {
    return null
  }
  const [, year, month, day] = match
  // Local midnight, deliberately: see the timezone note above.
  const date = new Date(Number(year), Number(month) - 1, Number(day))
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * What VDatePicker emitted -> the 'YYYY-MM-DD' string the field stores.
 *
 * Empty becomes '' rather than null, matching the normalisation
 * datePickerChanged already applies when the clearable X sets a date field to
 * null: the specs' `start` for these fields is '', and isDirty compares
 * value against start.
 */
export function dateToIsoDateString(value: unknown): string {
  if (typeof value === 'string') {
    // Already a field-shaped value (or a full ISO datetime): keep the date head.
    const match = ISO_DATE_HEAD.exec(value)
    return match ? match[0] : ''
  }
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return ''
  }
  const year = String(value.getFullYear()).padStart(4, '0')
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
